import { useTranslation } from 'react-i18next';

import CardData from '../cardData';
import {
  Briefcase,
  DocumentText,
  DollarCircle,
  Clock,
  User,
  Badge,
  Dashboard,
  Edit,
  DocumentBriefcase,
  DocumentCopy,
  MenuBoard,
  ArrowRight,
  WarningCircle,
} from '../../icons';
import { useFetchTransactionById } from '../../../hooks/useFetchTransactionById';
import { useParams } from 'react-router-dom';
import { useLanguage } from '../../../context/LanguageContext';
import { UI_LANGUAGE_TAGS, ROUTES, SystemConstants, UserType } from '../../../constants/constants';
import { useEffect, useState } from 'react';
import { useTransaction } from '../../../context/TransactionContext';
import {
  cardBorderColorMapping,
  InvoiceableType,
  tagColorMapping,
  TransactionStatus,
} from '../../../constants/transaction';
import { useTransactionParameters } from '../../../context/TransactionParameterContext';
import {
  formatter,
  getLocalDateAndTime,
  getMatterIconClass,
  getMatterLabel,
  getTotalPrice,
  getUnitPrice,
} from '../../../constants/utilityFunctions';
import { useUser } from '../../../context/UserContext';
import { ITransaction } from '../../../interface/transaction';
import { useCopyTransaction } from '../../../hooks/useCopyTransaction';
import RenderTransactionRestrictions from '../addTransaction/renderTransactionRestrictions';
import { Button, CardCta, Divider, Paragraph, Text, Tooltip } from '../../Ant';
import { PrimaryCard } from '../../../assets/styles/components/dashboard.styled';
import { Flex } from 'antd';
import DescriptionModal from '../../descriptionModal/descriptionModal';
import { useNavigationStack } from '../../../context/NavigationStackContext';
import { HoursDurationDisplay } from '../../hoursDurationDisplay/hoursDurationDisplay';
import NoteIcons from '../../common/NoteIcons';
import SkeletonEntityCard from '../../common/skeleton/skeletonEntityCard';
import { getViewLabel } from '../../../helpers/viewLabelHelper';

const TransactionDetails = () => {
  const { t } = useTranslation();
  const { language, getAppLanguageCode } = useLanguage();
  const { user } = useUser();
  const { push } = useNavigationStack();
  const { updateTransaction } = useTransaction();
  const { parameters } = useTransactionParameters();
  const { id: transactionId } = useParams();
  const [isShowMoreModalOpen, setIsShowMoreModalOpen] = useState<boolean>(false);
  const [showMoreContent, setShowMoreContent] = useState<
    { title: string; content: string } | undefined
  >(undefined);
  const [copyClicked, setCopyClicked] = useState<boolean>();
  const [selectedTransaction, setSelectedTransaction] = useState<ITransaction>();
  const { copyTransaction, startTransactionOnSameMatter } = useCopyTransaction({
    setCopyClicked,
    setSelectedTransaction,
  });
  const baseCurrency = parameters?.currency;
  const { data: transaction, isLoading: isTransactionLoading } = useFetchTransactionById({
    id: transactionId ?? '',
    relations: [
      'matter:matterNumber,subject,currency,invoiceableType,language',
      'matterPhase',
      'principalCompany:name',
      'principalPrivatePerson:fullName',
      'transactionUser:fullName',
      'transactionType:translations,standardPrice,ignoreDiscount',
      'skill:name',
      'unit:translations',
    ],
    fields: [
      'billableQuantity',
      'totalPrice',
      'totalPriceInCurrency',
      'workedHours',
      'unitId',
      'unitPrice',
      'unitPriceInCurrency',
      'status',
      'subject',
      'transactionDate',
      'matterId',
      'hasImportantNotes',
      'hasNotes',
      'totalPriceSetManually',
    ],
  });
  const hidePricingInfo =
    user?.userRole?.hidePricingInformation === true ||
    user?.userType === UserType.RESTRICTED ||
    transaction?.matter?.invoiceableType === InvoiceableType.INTERNAL;
  const unitTranslationName =
    transaction?.unit?.translations?.find((t) => t.language === getAppLanguageCode())
      ?.translation ?? '-';
  const showEdit = [TransactionStatus.nonBillable, TransactionStatus.open].includes(
    (transaction?.status ?? '') as TransactionStatus,
  );
  const getNameTranslation = () => {
    const langCode = UI_LANGUAGE_TAGS[language];
    const translation = transaction?.transactionType?.translations?.find(
      (t: { language: string }) => t.language === langCode,
    );
    return translation?.nameTranslation ?? '-';
  };

  const handleEditTransaction = () => {
    updateTransaction(null);
    setSelectedTransaction(transaction);
  };
  const handleCopyTransaction = () => {
    if (transaction) {
      copyTransaction(transaction);
    }
  };
  const handleNewToSameMatter = () => {
    if (transaction) {
      startTransactionOnSameMatter(transaction);
    }
  };
  useEffect(() => {
    if (transaction) {
      updateTransaction(transaction);
    }
  }, [transaction]);
  const handleSelectTransaction = (transaction?: ITransaction) => {
    if (transaction && !copyClicked) {
      push(`${ROUTES.ADD_EDIT_TRANSACTION}/${transaction.id}`);
    } else if (transaction && copyClicked) {
      push(ROUTES.ADD_EDIT_TRANSACTION);
    } else {
      setSelectedTransaction(undefined);
    }
  };
  const handleClose = () => {
    setIsShowMoreModalOpen(false);
  };
  const handleGoToMatter = () => {
    push(`${ROUTES.MATTER_DETAIL}/${transaction?.matter?.id}`);
  };
  if (isTransactionLoading)
    return (
      <SkeletonEntityCard
        rows={[1, 1, 2, 1, 2, 1, 1]}
        withSubcontentOnFirstRow
        className="py-10 px-10 h-full"
      />
    );
  return (
    <>
      <PrimaryCard
        data-testid="transaction-details-card"
        $isDetail
        className={`d-flex flex-col gap-10 h-full ${cardBorderColorMapping[transaction?.status as TransactionStatus]}`}
      >
        <Flex justify="space-between" align="center" className="w-full">
          <Flex gap={10} align="center">
            <Flex
              data-testid="transaction-status-chip"
              justify="center"
              align="center"
              className={`status-chip ${tagColorMapping[transaction?.status as TransactionStatus]}`}
            >
              {transaction?.status &&
                t(
                  `transaction:status.${Object.keys(TransactionStatus).find((key) => TransactionStatus[key as keyof typeof TransactionStatus] === transaction?.status) ?? transaction?.status}`,
                )}
            </Flex>
            <NoteIcons
              hasImportantNotes={transaction?.hasImportantNotes}
              hasNotes={transaction?.hasNotes}
            />
          </Flex>
          <Paragraph
            data-testid="transaction-date"
            $fontsize="12px"
            $color="subtitle"
            $lineheight="20px"
            className="ta-right"
          >
            {getLocalDateAndTime(new Date(transaction?.transactionDate ?? ''), true)}
          </Paragraph>
        </Flex>
        <Divider />
        <Flex vertical gap={10} className="flex-grow">
          <CardData
            icon={
              <Briefcase
                className={getMatterIconClass(transaction?.matter?.invoiceableType ?? '')}
              />
            }
            type="matter"
            title={getMatterLabel(t, transaction?.matter?.invoiceableType ?? '')}
            content={
              transaction?.principalCompany?.name ??
              transaction?.principalPrivatePerson?.fullName ??
              ''
            }
            subcontent={
              (transaction?.matter?.matterNumber ?? '') +
              ' - ' +
              (transaction?.matter?.subject ?? '')
            }
            contentclass="lineclamp-1"
            subcontentclass="lineclamp-2"
            headingcontent={
              transaction?.matter && (
                <Button
                  data-testid="view-matter-button"
                  type="text"
                  icon={<ArrowRight />}
                  iconPosition="end"
                  className="text-cta"
                  onClick={handleGoToMatter}
                >
                  {getViewLabel(t, 'matter:matter')}
                </Button>
              )
            }
          />
          <CardData
            type="transaction-subject"
            icon={<DocumentText />}
            title={t('transaction:transaction')}
            content={transaction?.subject ?? ''}
            contentclass="fw-400 lineclamp-2"
            moreAction={
              <Button
                data-testid="show-more-transaction-button"
                type="text"
                className="text-cta show-more"
                onClick={() => {
                  setShowMoreContent({
                    title: t('transaction:transaction'),
                    content: transaction?.subject ?? '',
                  });
                  setIsShowMoreModalOpen(true);
                }}
              >
                {t('common:more')}
              </Button>
            }
          />

          <Flex justify="space-between">
            {transaction?.unitId && transaction?.unitId === SystemConstants.UNIT_HOUR ? (
              <HoursDurationDisplay
                title={t('transaction:billableHours')}
                durationInHours={transaction.billableQuantity ?? 0}
              />
            ) : (
              <CardData
                type="hours"
                icon={<Clock />}
                title={t('transaction:billableAmount') + ` (${unitTranslationName})`}
                content={formatter.regionalNumberFormat({
                  value: transaction?.billableQuantity ?? 0,
                  minimumFractionDigits: 2,
                })}
              />
            )}
            {!hidePricingInfo && (
              <CardData
                icon={<DollarCircle />}
                type="price"
                title={
                  (transaction?.unitId === SystemConstants.UNIT_HOUR
                    ? t('transaction:hourPrice')
                    : t('transaction:unitPrice')) + ` (${transaction?.matter?.currency ?? ''})`
                }
                content={getUnitPrice(baseCurrency ?? '', transaction)}
              />
            )}
          </Flex>
          <Flex justify="space-between">
            <HoursDurationDisplay
              type="worked-hours"
              title={t('transaction:workedHours')}
              durationInHours={transaction?.workedHours ?? 0}
            />
            <CardData
              icon={<User />}
              title={t('common:author')}
              type="author"
              content={transaction?.transactionUser?.fullName ?? ''}
            />
          </Flex>
          {!hidePricingInfo && (
            <Flex className="total-transaction  justify-between">
              <Flex align="center" gap={5}>
                <Paragraph
                  $fontsize="12px"
                  $color="title"
                  $weight={500}
                  $lineheight="18px"
                  data-testid="total-price"
                >
                  {t('transaction:totalPrice')} {`(${transaction?.matter?.currency ?? ''})`}
                </Paragraph>
                {transaction?.totalPriceSetManually && (
                  <Tooltip
                    title={t('transaction:totalPriceSetManually')}
                    arrow={false}
                    data-testid="total-price-warning-tooltip"
                    placement="top"
                  >
                    <div className="cursor-pointer">
                      <WarningCircle className="fz-14 text-clr-beige-200" />
                    </div>
                  </Tooltip>
                )}
              </Flex>
              <Text
                data-testid="total-price"
                $fontsize="12px"
                $color="title"
                $weight={600}
                $lineheight="18px"
              >
                {getTotalPrice(baseCurrency ?? '', transaction)}
              </Text>
            </Flex>
          )}

          <CardData
            type="transaction-type"
            icon={<Dashboard />}
            title={t('transaction:type')}
            content={getNameTranslation()}
          />
          <CardData
            icon={<Badge />}
            title={t('transaction:requiredSkill')}
            content={transaction?.skill?.name ?? '-'}
            type="skill"
          />
          <CardData
            type="transaction-matter-phase"
            icon={<MenuBoard />}
            title={t('transaction:matterPhase')}
            content={transaction?.matterPhase?.name ?? '-'}
          />
        </Flex>
        <Flex vertical gap={10} className="w-full">
          <Divider />
          <Flex gap={15}>
            <CardCta data-testid="new-to-same-matter-button" onClick={handleNewToSameMatter}>
              <DocumentBriefcase />
              <Paragraph $fontsize="10px" $color="description" $weight={400} $lineheight="130%">
                {t('transaction:newToSameMatter')}
              </Paragraph>
            </CardCta>

            <CardCta data-testid="copy-transaction-button" onClick={handleCopyTransaction}>
              <DocumentCopy />
              <Paragraph $fontsize="10px" $color="description" $weight={400} $lineheight="130%">
                {t('transaction:copyTransaction')}
              </Paragraph>
            </CardCta>

            {showEdit && (
              <CardCta data-testid="edit-transaction-button" onClick={handleEditTransaction}>
                <Edit />
                <Paragraph $fontsize="10px" $color="description" $weight={400} $lineheight="130%">
                  {t('transaction:editTransaction')}
                </Paragraph>
              </CardCta>
            )}
          </Flex>
        </Flex>
      </PrimaryCard>
      <RenderTransactionRestrictions
        transaction={selectedTransaction}
        onSelectTransaction={handleSelectTransaction}
      />
      {/* Stays mounted through its closing animation, which is when antd hands focus back. */}
      <DescriptionModal
        title={showMoreContent?.title ?? ''}
        description={showMoreContent?.content ?? ''}
        open={isShowMoreModalOpen}
        onClose={handleClose}
      />
    </>
  );
};

export default TransactionDetails;
